import {
  GRID_COLUMNS,
  TileLayout,
} from "features/Dashboard/types/dashboardDocument"

/** Reference canvas width, in px, the grid units are measured against. */
const REF_WIDTH = 1200
const MARGIN = 12
const ROW_PX = 40
/** 87 px: twelve columns and thirteen margins fill the reference width. */
const COL_PX = (REF_WIDTH - MARGIN * (GRID_COLUMNS + 1)) / GRID_COLUMNS

export interface Rect {
  x: number
  y: number
  w: number
  h: number
}

/**
 * A run of grid rows drawn on one slide: from row `top`, `rows` rows tall.
 * No tile crosses either edge of a band.
 */
export interface Band {
  top: number
  rows: number
}

const round3 = (n: number): number => Math.round(n * 1000) / 1000

/** A grid row and a grid margin at full content width, in inches. */
const gridUnits = (content: Rect) => {
  const inPerPx = content.w / REF_WIDTH
  return {
    colIn: COL_PX * inPerPx,
    rowIn: ROW_PX * inPerPx,
    marginIn: MARGIN * inPerPx,
  }
}

/** Height of `rows` grid rows and their margins at full width, in inches. */
const rowsHeight = (rows: number, content: Rect): number => {
  const { rowIn, marginIn } = gridUnits(content)
  return rows * rowIn + (rows + 1) * marginIn
}

/** How many grid rows fit the content height at full width (scale 1). */
export const rowsThatFit = (content: Rect): number => {
  const { rowIn, marginIn } = gridUnits(content)
  return Math.max(1, Math.floor((content.h - marginIn) / (rowIn + marginIn) + 1e-9))
}

/**
 * A page's tiles cut into bands, one slide each, top to bottom. A band
 * ends at a row boundary no tile crosses, and is as many rows as fit the
 * content height at full width. When no such boundary lies within that
 * height (one very tall tile, or tiles overlapping every cut), the band
 * runs to the first boundary that no tile crosses, and is scaled down to
 * fit when drawn (see `layoutToSlideRect`). Empty rows between bands are
 * left out. A page with no tiles has no bands.
 */
export const pageBands = (layouts: TileLayout[], content: Rect): Band[] => {
  const fit = rowsThatFit(content)
  const bands: Band[] = []
  let rest = layouts
  while (rest.length > 0) {
    const top = Math.min(...rest.map((l) => l.y))
    const crossed = (cut: number) =>
      rest.some((l) => l.y < cut && l.y + l.h > cut)
    const cuts = Array.from(new Set(rest.map((l) => l.y + l.h)))
      .filter((cut) => !crossed(cut))
      .sort((a, b) => a - b)
    const within = cuts.filter((cut) => cut - top <= fit)
    const end = within.length > 0 ? within[within.length - 1] : cuts[0]
    bands.push({ top, rows: end - top })
    rest = rest.filter((l) => l.y >= end)
  }
  return bands
}

/** Whether a tile starts in `band` (and so, as no tile crosses a cut, lies in it). */
export const inBand = (layout: TileLayout, band: Band): boolean =>
  layout.y >= band.top && layout.y < band.top + band.rows

/**
 * A tile's grid layout as a slide rectangle, in inches, on its band's
 * slide. The twelve columns and their margins span the content width;
 * rows keep the 40 px to 87 px ratio of the reference grid and count from
 * the band's top. A band taller than the content area scales down
 * uniformly so it fits, centred across the content width.
 */
export const layoutToSlideRect = (
  layout: TileLayout,
  band: Band,
  content: Rect
): Rect => {
  const { colIn, rowIn, marginIn } = gridUnits(content)
  const usedH = rowsHeight(band.rows, content)
  const scale = usedH > content.h ? content.h / usedH : 1
  const left = content.x + (content.w * (1 - scale)) / 2
  const row = layout.y - band.top
  const x = left + (marginIn + layout.x * (colIn + marginIn)) * scale
  const y = content.y + (marginIn + row * (rowIn + marginIn)) * scale
  const w = (layout.w * colIn + (layout.w - 1) * marginIn) * scale
  const h = (layout.h * rowIn + (layout.h - 1) * marginIn) * scale
  return { x: round3(x), y: round3(y), w: round3(w), h: round3(h) }
}
